import api from './api';
const AI_TIMEOUT = Number(process.env.REACT_APP_AI_TIMEOUT_MS || '') || 90000;

export interface AIGenerateBlogReq {
  prompt: string;
  audience?: string;
  min_words?: number;
  model?: string;
}

export interface AIGenerateBlogResp {
  title: string;
  slug: string;
  html: string;
}

export async function generateBlogAI(payload: AIGenerateBlogReq): Promise<AIGenerateBlogResp> {
  const { data } = await api.post<AIGenerateBlogResp>('/ai/blog/generate', payload, { timeout: AI_TIMEOUT });
  
  // Handle potential JSON string response from AI (defensive parsing)
  let parsedData = data;
  if (typeof data === 'string') {
    try {
      parsedData = JSON.parse(data);
    } catch {
      throw new Error('AI vrátila neplatný formát odpovědi');
    }
  }
  
  return parsedData;
}

// Instagram generation
export interface AIGenerateInstagramMatch {
  home?: string;
  away?: string;
  competition?: string;
  date_time?: string;
  venue?: string;
  score?: string;
}

export interface AIGenerateInstagramReq {
  type?: 'article' | 'event' | 'generic' | string;
  title?: string;
  content?: string;
  club_name?: string;
  link: string;
  hashtags?: string[];
  audience?: string;
  tone?: string;
  category?: string;
  match?: AIGenerateInstagramMatch | null;
}

export interface AIGenerateInstagramResp { text: string }

export async function generateInstagramAI(payload: AIGenerateInstagramReq): Promise<AIGenerateInstagramResp> {
  const { data } = await api.post<AIGenerateInstagramResp>('/ai/instagram/generate', payload, { timeout: AI_TIMEOUT });
  let parsed: any = data;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { parsed = { text: '' }; }
  }
  return parsed as AIGenerateInstagramResp;
}

export interface AIGenerateInstagramImagesReq {
  prompt: string;
  aspect?: string;
  count?: number;
}

export interface AIGenerateInstagramImagesResp {
  urls: string[];
}

export async function generateInstagramImagesAI(payload: AIGenerateInstagramImagesReq): Promise<AIGenerateInstagramImagesResp> {
  const { data } = await api.post<AIGenerateInstagramImagesResp>('/ai/instagram/images', payload, { timeout: AI_TIMEOUT });
  let parsed: any = data;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { parsed = { urls: [] }; }
  }
  return parsed as AIGenerateInstagramImagesResp;
}

export interface AIGenerateMainImageReq {
  subject?: 'article' | 'event' | 'activity' | string;
  title: string;
  category?: string;
  club_name?: string;
  primary_color?: string;
  secondary_color?: string;
}

export interface AIGenerateMainImageResp {
  url: string;
}

export async function generateMainImageAI(payload: AIGenerateMainImageReq): Promise<AIGenerateMainImageResp> {
  const { data } = await api.post<AIGenerateMainImageResp>('/ai/main-image/generate', payload, { timeout: AI_TIMEOUT });
  let parsed: any = data;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { parsed = { url: '' }; }
  }
  return parsed as AIGenerateMainImageResp;
}

export interface AIGenerateCSSReq {
  prompt: string;
  element_name?: string;
  root_selector?: string;
  current_css?: string;
  current_styles?: Record<string, any>;
  theme?: Record<string, string>;
  breakpoints?: number[];
  context?: Record<string, any>;
}

export interface AIGenerateCSSResp {
  css: string;
}

export async function generateCSSAI(payload: AIGenerateCSSReq): Promise<AIGenerateCSSResp> {
  const { data } = await api.post<AIGenerateCSSResp>('/ai/css/generate', payload, { timeout: AI_TIMEOUT });
  let parsed = data as any;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { parsed = { css: '' }; }
  }
  return parsed as AIGenerateCSSResp;
}

export interface AIGenerateAboutReq {
  prompt: string;
  club_name?: string;
  style?: string;
  audience?: string;
  model?: string;
}

export interface AIGenerateAboutResp {
  title: string;
  subtitle: string;
  html: string;
  seo_title: string;
  seo_description: string;
}

export async function generateAboutAI(payload: AIGenerateAboutReq): Promise<AIGenerateAboutResp> {
  const { data } = await api.post<AIGenerateAboutResp>('/ai/about/generate', payload, { timeout: AI_TIMEOUT });
  
  // Handle potential JSON string response from AI (defensive parsing)
  let parsedData = data;
  if (typeof data === 'string') {
    try {
      parsedData = JSON.parse(data);
    } catch {
      throw new Error('AI vrátila neplatný formát odpovědi');
    }
  }
  
  return parsedData;
}

export interface AIOCRReq {
  document_url?: string;
  image_url?: string;
  pages?: number[];
  model?: string;
}

export interface AIOCRResp {
  text: string;
}

export async function processOcrAI(payload: AIOCRReq): Promise<AIOCRResp> {
  const { data } = await api.post<AIOCRResp>('/ai/ocr/process', payload, { timeout: AI_TIMEOUT });
  let parsed: any = data;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { parsed = { text: '' }; }
  }
  return parsed as AIOCRResp;
}

export interface AITranscriptionReq {
  file_url?: string;
  file_id?: string;
  language?: string;
  model?: string;
}

export interface AITranscriptionResp {
  text: string;
  language: string;
}

export async function transcribeAudioAI(payload: AITranscriptionReq): Promise<AITranscriptionResp> {
  const { data } = await api.post<AITranscriptionResp>('/ai/voice/transcribe', payload, { timeout: AI_TIMEOUT });
  let parsed: any = data;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { parsed = { text: '', language: '' }; }
  }
  return parsed as AITranscriptionResp;
}

export interface AITestParseReq {
  raw: string;
}

export interface AITestParseResp {
  sanitized: string;
  parsed?: any;
  error?: string;
}

export async function testAIParse(payload: AITestParseReq): Promise<AITestParseResp> {
  const { data } = await api.post<AITestParseResp>('/ai/test-parse', payload, { timeout: AI_TIMEOUT });
  return data;
}
